import { WidgetItem } from '../widgets/core/widget-contexto';

/** Quantidade de colunas da grade do dashboard. */
export const COLUNAS = 4;

/** Linhas vazias exibidas no final do dashboard no modo edição. */
export const LINHAS_EXTRAS_EDICAO = 2;

/** Widget no estado local do dashboard (FRONTEND.md §3.3). */
export interface ItemLocal extends WidgetItem {
  /** Chave estável para o `track` do template: `id` ou uma chave local para itens ainda não salvos. */
  chave: string;
  widgetId: string;
  linha: number;
  coluna: number;
  /** Identificador sem componente no WIDGET_REGISTRY (FRONTEND.md §4.6). */
  semComponente: boolean;
}

export interface Posicao {
  linha: number;
  coluna: number;
}

type Retangulo = Posicao & { largura: number, altura: number };
type Celula = `${number}:${number}`;

/** Células ("linha:coluna") ocupadas por um retângulo. */
export function celulas(linha: number, coluna: number, largura: number, altura: number): Celula[] {
  const resultado: Celula[] = [];
  for (let l = linha; l < linha + altura; l++) {
    for (let c = coluna; c < coluna + largura; c++) {
      resultado.push(`${l}:${c}`);
    }
  }
  return resultado;
}

/** Mapa das células ocupadas. Widgets sem componente também ocupam células. */
export function ocupacao<T extends Retangulo>(itens: T[], ignorar?: T): Map<Celula, T> {
  const mapa = new Map<Celula, T>();
  for (const item of itens) {
    if (item === ignorar) continue;
    for (const celula of celulas(item.linha, item.coluna, item.largura, item.altura)) {
      mapa.set(celula, item);
    }
  }
  return mapa;
}

/** Indica se um widget `largura × altura` cabe com o canto superior esquerdo em (linha, coluna). */
export function cabe(linha: number, coluna: number, largura: number, altura: number, mapa: Map<Celula, unknown>): boolean {
  if (linha < 0 || coluna < 0 || coluna + largura > COLUNAS) return false;
  return celulas(linha, coluna, largura, altura).every(c => !mapa.has(c));
}

/** Quantidade de linhas usadas pelos widgets (`max(linha + altura)`). */
export function linhasOcupadas(itens: Retangulo[]): number {
  return itens.reduce((maior, i) => Math.max(maior, i.linha + i.altura), 0);
}

/** Linhas renderizadas: até o último widget; no modo edição, mais 2 linhas vazias. */
export function totalLinhas(itens: Retangulo[], modoEdicao: boolean): number {
  return linhasOcupadas(itens) + (modoEdicao ? LINHAS_EXTRAS_EDICAO : 0);
}

/** Primeira posição livre em que o widget cabe, varrendo linha a linha, da esquerda para a direita. */
export function primeiraPosicaoLivre(itens: Retangulo[], largura: number, altura: number): Posicao {
  const mapa = ocupacao(itens);
  for (let linha = 0; ; linha++) {
    for (let coluna = 0; coluna + largura <= COLUNAS; coluna++) {
      if (cabe(linha, coluna, largura, altura, mapa)) return { linha, coluna };
    }
  }
}

/** Células livres da grade (slots com +), dentro das `linhas` renderizadas. */
export function slotsVazios(itens: Retangulo[], linhas: number): Posicao[] {
  const mapa = ocupacao(itens);
  const slots: Posicao[] = [];
  for (let linha = 0; linha < linhas; linha++) {
    for (let coluna = 0; coluna < COLUNAS; coluna++) {
      if (!mapa.has(`${linha}:${coluna}`)) slots.push({ linha, coluna });
    }
  }
  return slots;
}

/** Ordem de exibição no celular: `linha` crescente e, na mesma linha, `coluna` crescente. */
export function ordemMobile<T extends Posicao>(itens: T[]): T[] {
  return [...itens].sort((a, b) => a.linha - b.linha || a.coluna - b.coluna);
}
